/**
 * Writing a change to the shared board.
 *
 * Every tool that changes work already on the board — the eraser, the select tool —
 * says what it did in the same three words: what went, what arrived, and what was
 * swapped for a changed version of itself. All of it is written in one transaction,
 * so a whole sweep of the eraser or a whole drag of a selection is a single press of
 * undo (SRS C6) and reaches everyone else as one change.
 *
 * Nothing here is in the way of the hand. The board on this screen has already
 * changed by the time any of it runs (docs/03-ARCHITECTURE.md §7.2).
 */

import {
  findObject,
  putObject,
  putStroke,
  removeObject,
  removeStroke,
  replaceStroke,
  transact,
  updateObject,
} from '@opal/board-doc';
import type { BoardObject, Stroke, Unsubscribe } from '@opal/shared';
import type * as Y from 'yjs';

import { objectFromStroke } from './object-view.js';
import { isObjectId } from './shape-object.js';
import type { BoardBridge } from './sync.js';

/** One change to work already on the board. */
export interface BoardEdit {
  /** Ids of what is gone — strokes and objects alike, told apart by the id itself. */
  readonly removed: readonly string[];
  /** Strokes that are new to the board. */
  readonly added: readonly Stroke[];
  /**
   * Changed in place: same id, same position, different points or colour.
   *
   * A shape travels here as its stroke-shaped view (shape-object.ts), which is what
   * lets the select tool move and recolour shapes and ink with the same code.
   */
  readonly replaced: readonly Stroke[];
  /** SRS C4 — objects new to the board: a shape, a text box, a sticky note. */
  readonly objectsAdded: readonly BoardObject[];
  /**
   * Objects changed in place: same id, different contents.
   *
   * Separate from `replaced` because what changed cannot always be said as a stroke.
   * Words typed into a sticky note are not a line of points, and there is no honest
   * way to describe them as one.
   */
  readonly objectsChanged: readonly BoardObject[];
}

/** A tool that changes work already on the board. */
export interface EditSource {
  onEdited(handler: (edit: BoardEdit) => void): Unsubscribe;
}

/** Nothing changed. Used as the starting point and as the "not worth sending" answer. */
export const NO_EDIT: BoardEdit = {
  removed: [],
  added: [],
  replaced: [],
  objectsAdded: [],
  objectsChanged: [],
};

/** True when an edit would not change anything. */
export function isEmptyEdit(edit: BoardEdit): boolean {
  return (
    edit.removed.length === 0 &&
    edit.added.length === 0 &&
    edit.replaced.length === 0 &&
    edit.objectsAdded.length === 0 &&
    edit.objectsChanged.length === 0
  );
}

/** The part of the board document an edit touches. */
export interface EditTarget {
  readonly doc: Y.Doc;
  /** Null until the board has arrived. */
  readonly pageId: string | null;
  /** Strokes still waiting for a page to go on. Edited in place. */
  readonly waiting: Stroke[];
  /** Objects waiting for the same thing. */
  readonly waitingObjects: BoardObject[];
  /** Ids this device put on the board, so its own work is not handed back to it. */
  readonly mine: Set<string>;
  /** Marks the change as this person's own, for per-user undo. */
  readonly origin: unknown;
}

/** Takes strokes that never reached the board out of the queue instead. */
function unqueue(target: EditTarget, removed: readonly string[]): Set<string> {
  const gone = new Set(removed);
  const held = target.waiting.filter((stroke) => gone.has(stroke.id));
  for (const stroke of held) target.waiting.splice(target.waiting.indexOf(stroke), 1);
  return new Set(held.map((stroke) => stroke.id));
}

/** Swaps a stroke still waiting to be sent for its changed version. */
function amendQueued(target: EditTarget, replaced: readonly Stroke[]): Set<string> {
  const queued = new Set<string>();
  for (const stroke of replaced) {
    const at = target.waiting.findIndex((held) => held.id === stroke.id);
    if (at < 0) continue;
    target.waiting[at] = stroke;
    queued.add(stroke.id);
  }
  return queued;
}

/** Takes objects that never reached the board out of the queue instead. */
function unqueueObjects(target: EditTarget, removed: readonly string[]): Set<string> {
  const gone = new Set(removed);
  const held = target.waitingObjects.filter((object) => gone.has(object.id));
  for (const object of held) {
    target.waitingObjects.splice(target.waitingObjects.indexOf(object), 1);
  }
  return new Set(held.map((object) => object.id));
}

/**
 * Writes a changed object back to the board.
 *
 * It arrives as the stroke-shaped view the select tool worked on, so what it is — a
 * pentagon, an arrow, the words on a sticky note — is read back off the object already
 * on the board and kept. Only where it sits and how it looks change.
 */
function writeMovedObject(doc: Y.Doc, pageId: string, stroke: Stroke, origin: unknown): void {
  const existing = findObject(doc, pageId, stroke.id);
  if (!existing) return;
  updateObject(doc, pageId, stroke.id, objectFromStroke(existing, stroke), origin);
}

/** Swaps an object still waiting to be sent for its changed version. */
function amendQueuedObjects(target: EditTarget, changed: readonly BoardObject[]): Set<string> {
  const queued = new Set<string>();
  for (const object of changed) {
    const at = target.waitingObjects.findIndex((held) => held.id === object.id);
    if (at < 0) continue;
    target.waitingObjects[at] = object;
    queued.add(object.id);
  }
  return queued;
}

/** Everything the edit removes, whether it is a stroke or an object. */
function writeRemovals(
  target: EditTarget,
  pageId: string,
  edit: BoardEdit,
  held: Set<string>,
): void {
  for (const id of edit.removed) {
    if (held.has(id)) continue;
    if (isObjectId(id)) removeObject(target.doc, pageId, id, target.origin);
    else removeStroke(target.doc, pageId, id, target.origin);
  }
}

/**
 * Applies one edit.
 *
 * A stroke drawn a moment ago may still be waiting for the page to arrive, in which
 * case changing it means changing what is about to be sent, and erasing it means
 * simply never sending it.
 */
export function applyEdit(target: EditTarget, edit: BoardEdit): void {
  const held = unqueue(target, edit.removed);
  const heldObjects = unqueueObjects(target, edit.removed);
  const queued = amendQueued(target, edit.replaced);
  const queuedObjects = amendQueuedObjects(target, edit.objectsChanged);

  const pageId = target.pageId;
  if (!pageId) {
    target.waiting.push(...edit.added);
    target.waitingObjects.push(...edit.objectsAdded);
    return;
  }

  transact(
    target.doc,
    () => {
      writeRemovals(target, pageId, edit, new Set([...held, ...heldObjects]));
      for (const stroke of edit.replaced) {
        if (queued.has(stroke.id)) continue;
        if (isObjectId(stroke.id)) writeMovedObject(target.doc, pageId, stroke, target.origin);
        else replaceStroke(target.doc, pageId, stroke, target.origin);
      }
      for (const stroke of edit.added) {
        target.mine.add(stroke.id);
        putStroke(target.doc, pageId, stroke, target.origin);
      }
      for (const object of edit.objectsAdded) {
        putObject(target.doc, pageId, object, target.origin);
      }
      for (const object of edit.objectsChanged) {
        if (queuedObjects.has(object.id)) continue;
        updateObject(target.doc, pageId, object.id, object, target.origin);
      }
    },
    target.origin,
  );
}

/**
 * Joins a tool that changes the board to the shared copy of it.
 *
 * One way only: a change made here is written to the board, and a change made
 * anywhere — including here — arrives back through the handlers the ink already
 * listens on, so there is nothing extra to subscribe to.
 */
export function bindEditsToBoard(source: EditSource, bridge: BoardBridge): Unsubscribe {
  return source.onEdited((edit) => {
    bridge.edit(edit);
  });
}
